// ==========================================================================
// lib/receipts.ts — receipts: capture, inbox, reader, matching (FF-044, 9 Oct 2026)
//   The file goes to the Worker (R2); the details live in Supabase.
//   The reader (FF-035) only suggests; nothing is saved until Save is pressed.
// ==========================================================================
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useHouseholdId, useMe } from "./household";
import { readOnlyPreview } from "./previewSafety";
import type { Txn } from "./transactions";

/** The Worker: localhost:8787 when developing, VITE_WORKER_URL once live. */
export const WORKER_URL: string = (import.meta.env.VITE_WORKER_URL as string | undefined) ?? (import.meta.env.DEV ? "http://localhost:8787" : "");

export type Receipt = {
  id: string; file_key: string; file_name: string | null; mime: string | null; size_bytes: number | null; uploaded: boolean;
  contact_id: string | null; receipt_date: string | null; total_cents: number | null; gst_cents: number | null;
  category_id: string | null; tracker_id: string | null; asset_id: string | null; paid_by: "us" | "company" | "other";
  note: string | null; transaction_id: string | null; ai_read_at: string | null; archived_at: string | null; created_at: string;
  // Bills (033): to pay until matched to the bank line or marked paid.
  is_bill?: boolean; due_on?: string | null; paid_on?: string | null;
};
export const billPending = (r: Receipt) => !!r.is_bill && !r.transaction_id && !r.paid_on && !r.archived_at;
export type Suggested = { merchant?: string | null; date?: string | null; total?: number | null; gst?: number | null; card_last4?: string | null; code?: string | null; summary?: string | null };

async function token() {
  const { data } = await supabase.auth.getSession();
  const t = data.session?.access_token;
  if (!t) throw new Error("Signed out. Sign in again.");
  return t;
}

export async function worker(path: string, init: RequestInit = {}) {
  if (readOnlyPreview && (init.method ?? "GET").toUpperCase() !== "GET") throw new Error("Read-only preview: uploads and Worker actions are disabled.");
  if (!WORKER_URL) throw new Error("The file service isn't set up yet.");
  let r: Response;
  try { r = await fetch(`${WORKER_URL}${path}`, { ...init, headers: { ...(init.headers ?? {}), Authorization: `Bearer ${await token()}` } }); }
  catch { throw new Error("Can't reach the file service. Is `pnpm dev:worker` running?"); }
  return r;
}

export function useWorkerHealth() {
  return useQuery({
    queryKey: ["worker_health"], retry: false, refetchInterval: 30000,
    queryFn: async () => {
      try { const r = await fetch(`${WORKER_URL}/health`); return (await r.json()) as { ok: boolean; reader: boolean }; }
      catch { return { ok: false, reader: false }; }
    },
  });
}

export function useReceipts() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["receipts", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("receipts")
        .select("id,file_key,file_name,mime,size_bytes,uploaded,contact_id,receipt_date,total_cents,gst_cents,category_id,tracker_id,asset_id,paid_by,note,transaction_id,ai_read_at,archived_at,created_at,is_bill,due_on,paid_on")
        .eq("household_id", hid).order("created_at", { ascending: false }).limit(1000);
      if (error) throw error;
      return (data as Receipt[]).map((r) => ({ ...r, total_cents: r.total_cents === null ? null : Number(r.total_cents), gst_cents: r.gst_cents === null ? null : Number(r.gst_cents) }));
    },
  });
}

function useInv() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return () => { for (const k of ["receipts", "audit", "unreconciled", "txns", "split_totals", "rec_summary"]) void qc.invalidateQueries({ queryKey: [k, hid] }); };
}

/** Big phone photos are shrunk to 2000px JPEG before upload (PDFs and HEIC go as they are). */
export async function shrink(f: File): Promise<Blob> {
  if (!["image/jpeg", "image/png", "image/webp"].includes(f.type) || f.size < 1_500_000) return f;
  try {
    const bmp = await createImageBitmap(f);
    const k = Math.min(1, 2000 / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas"); c.width = Math.round(bmp.width * k); c.height = Math.round(bmp.height * k);
    c.getContext("2d")!.drawImage(bmp, 0, 0, c.width, c.height);
    const b = await new Promise<Blob | null>((res) => c.toBlob(res, "image/jpeg", 0.82));
    return b && b.size < f.size ? b : f;
  } catch { return f; }
}

export function useAddReceipts() {
  const hid = useHouseholdId(); const me = useMe(); const inv = useInv();
  return useMutation({
    mutationFn: async (files: File[]) => {
      const failed: string[] = [];
      for (const f of files) {
        const id = crypto.randomUUID();
        const body = await shrink(f);
        const type = body.type || f.type || (f.name.toLowerCase().endsWith(".pdf") ? "application/pdf" : "");
        const { error } = await supabase.from("receipts").insert({ id, household_id: hid, file_key: `${hid}/receipts/${id}`, file_name: f.name, uploaded_by: me?.id ?? null });
        if (error) { failed.push(f.name); continue; }
        const r = await worker(`/receipts/${id}/file`, { method: "PUT", headers: { "Content-Type": type }, body });
        if (!r.ok) { const j = await r.json().catch(() => ({})) as { message?: string }; failed.push(`${f.name}${j.message ? ` (${j.message})` : ""}`); }
      }
      if (failed.length) throw new Error(`Couldn't add: ${failed.join(", ")}`);
    },
    onSettled: inv,
  });
}

export function useSaveReceipt() {
  const inv = useInv();
  return useMutation({
    mutationFn: async ({ id, r }: { id: string; r: Partial<Receipt> }) => {
      const { error } = await supabase.from("receipts").update(r).eq("id", id);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

/** Ask the reader for suggestions. Throws with a plain message when it's off or can't read. */
export async function readReceipt(id: string): Promise<Suggested> {
  const r = await worker(`/receipts/${id}/read`, { method: "POST" });
  const j = (await r.json().catch(() => ({}))) as { suggested?: Suggested; message?: string };
  if (!r.ok || !j.suggested) throw new Error(j.message ?? "The reader couldn't read it.");
  return j.suggested;
}

/** The file as a local blob URL (fetched with the session token). */
export function useReceiptFile(r: Receipt | undefined) {
  return useWorkerFile(r?.uploaded ? `/receipts/${r.id}/file` : null);
}

/** Any Worker-held file (receipt or agent statement) as a local blob URL. */
export function useWorkerFile(path: string | null) {
  const [url, setUrl] = useState<string | null>(null); const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true; let made: string | null = null;
    setUrl(null); setErr(false);
    if (!path) return;
    (async () => {
      try {
        const res = await worker(path);
        if (!res.ok) throw new Error();
        made = URL.createObjectURL(await res.blob());
        if (alive) setUrl(made);
      } catch { if (alive) setErr(true); }
    })();
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [path]);
  return { url, err };
}

/** A filled-in receipt waiting for its bank line: same amount, within 5 days, paid by us. */
export function receiptFor(t: Txn, receipts: Receipt[] | undefined): Receipt | undefined {
  if (t.amount_cents >= 0) return undefined;
  const d = new Date(t.posted_on).getTime(); const day = 86400000;
  // A receipt within 5 days of its date; a bill (033) any time from its bill date to a week past its due date.
  const fits = (r: Receipt) => {
    if (r.receipt_date && Math.abs(new Date(r.receipt_date).getTime() - d) <= 5 * day) return true;
    if (r.is_bill && r.due_on) { const due = new Date(r.due_on).getTime(); const from = r.receipt_date ? new Date(r.receipt_date).getTime() : due - 45 * day; return d >= from - day && d <= due + 7 * day; }
    return false;
  };
  const anchor = (r: Receipt) => new Date((r.is_bill && r.due_on) || r.receipt_date!).getTime();
  return (receipts ?? []).filter((r) => !r.archived_at && !r.transaction_id && !r.paid_on && r.paid_by === "us" && r.total_cents === -t.amount_cents && (r.receipt_date || r.due_on) && fits(r))
    .sort((a, b) => Math.abs(anchor(a) - d) - Math.abs(anchor(b) - d))[0];
}

/** A bill typed in (optionally with its PDF or photo). The file key is fixed even with no file, like any receipt. */
export function useAddBill() {
  const hid = useHouseholdId(); const me = useMe(); const inv = useInv();
  return useMutation({
    mutationFn: async (b: { file?: File; contact_id: string | null; total_cents: number | null; receipt_date: string | null; due_on: string; category_id: string | null; note: string | null }) => {
      const id = crypto.randomUUID();
      const { file, ...row } = b;
      const { error } = await supabase.from("receipts").insert({ ...row, id, household_id: hid, file_key: `${hid}/receipts/${id}`, file_name: file?.name ?? null, is_bill: true, uploaded_by: me?.id ?? null });
      if (error) throw error;
      if (file) {
        const body = await shrink(file);
        const type = body.type || file.type || (file.name.toLowerCase().endsWith(".pdf") ? "application/pdf" : "");
        const r = await worker(`/receipts/${id}/file`, { method: "PUT", headers: { "Content-Type": type }, body });
        if (!r.ok) { const j = await r.json().catch(() => ({})) as { message?: string }; throw new Error(`The bill is saved but the file didn't upload${j.message ? ` (${j.message})` : ""}.`); }
      }
      return id;
    },
    onSettled: inv,
  });
}
